// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/AppContext.tsx

import React, { createContext, useState, useContext, useCallback, ReactNode } from 'react';

import { API_BASE_URL } from './api';

// --- Shared Type Definitions ---
// FIX: Export all the types that other components need to import.
export interface Activity {
  message: string;
  icon: string;
  timestamp: string;
}

export interface Task {
  id: number;
  title: string;
  description: string | null;
}

export interface Board {
  id: number;
  name: string;
  type: string;
  tasks: Task[];
  project_id: number | null;
}

export interface ProjectDetails {
  id: number;
  name: string;
  created_at: string;
  created_by: string;
  plan_data: any;
  board: Board | null;
}

export interface NewBoardData {
  boardName: string;
  boardType: 'kanban' | 'scrum';
  linkedProjectId?: string;
}


// --- Context Type Definition ---
// FIX: Add all the state and functions that are used across the app.
interface AppContextType {
  // Global App State
  companyName: string;
  teamName: string;
  mode: 'light' | 'dark';
  teamMembers: string[];
  recentActivities: Activity[];
  toggleColorMode: () => void;
  addTeamMembers: (emails: string[]) => void;
  addActivity: (activity: Omit<Activity, 'timestamp'>) => void;
  clearActivities: () => void;

  // Project State
  projects: ProjectDetails[];
  isLoading: boolean;
  error: string | null;
  fetchProjects: (searchQuery?: string) => Promise<void>;
  fetchProjectById: (id: number) => Promise<ProjectDetails | null>;

  // Board State
  boards: Board[];
  fetchBoards: () => Promise<void>;
  createBoard: (data: NewBoardData) => Promise<Board | null>;
}

// --- Context Creation ---
const AppContext = createContext<AppContextType | undefined>(undefined);

// --- Custom Hook for easy consumption ---
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// --- Provider Component ---
const DEFAULT_COMPANY_NAME = 'IntechGambit';
const DEFAULT_TEAM_NAME = 'Pro-Maximizer';

export const AppProvider = ({ children }: { children: ReactNode }) => {
  // Global App State
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY_NAME);
  const [teamName, setTeamName] = useState(DEFAULT_TEAM_NAME);
  const [teamMembers, setTeamMembers] = useState(['amina.jackson@example.com', 'leo.vinci@example.com']);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);

  // Project and Board State
  const [projects, setProjects] = useState<ProjectDetails[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Global App Functions ---
  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const addActivity = (activity: Omit<Activity, 'timestamp'>) => {
    const newActivity = { ...activity, timestamp: new Date().toISOString() };
    setRecentActivities(prev => [newActivity, ...prev.slice(0, 4)]);
  };

  const clearActivities = () => setRecentActivities([]);

  const addTeamMembers = (emails: string[]) => {
    setTeamMembers(prev => Array.from(new Set([...prev, ...emails])));
    addActivity({ message: `Invited ${emails.length} new members`, icon: 'PersonAdd' });
  };

  // --- Project and Board Functions ---
  const fetchProjects = useCallback(async (searchQuery = '') => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/projects?search=${encodeURIComponent(searchQuery)}`);
      if (!response.ok) {
        throw new Error('Failed to fetch projects from the server.');
      }
      const data: ProjectDetails[] = await response.json();
      setProjects(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchProjectById = useCallback(async (id: number): Promise<ProjectDetails | null> => {
    setIsLoading(true);
    setError(null);
    try {
        const response = await fetch(`${API_BASE_URL}/projects/${id}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch project details. Status: ${response.status}`);
        }
        const data: ProjectDetails = await response.json();
        setProjects(prev => {
            const index = prev.findIndex(p => p.id === id);
            if (index !== -1) {
                const newProjects = [...prev];
                newProjects[index] = data;
                return newProjects;
            }
            return [data, ...prev.filter(p => p.id !== id)];
        });
        return data;
    } catch (err: any) {
        setError(err.message);
        return null;
    } finally {
        setIsLoading(false);
    }
  }, []);

  const fetchBoards = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
        const response = await fetch(`${API_BASE_URL}/api/boards`);
        if (!response.ok) {
            throw new Error('Failed to fetch boards.');
        }
        const data: Board[] = await response.json();
        setBoards(data);
    } catch (err: any) {
        setError(err.message);
    } finally {
        setIsLoading(false);
    }
  }, []);

  const createBoard = useCallback(async (data: NewBoardData): Promise<Board | null> => {
    setIsLoading(true);
    setError(null);
    try {
        const payload = {
            name: data.boardName,
            type: data.boardType,
            linked_project_id: data.linkedProjectId ? parseInt(data.linkedProjectId, 10) : null,
        };
        const response = await fetch(`${API_BASE_URL}/api/boards`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create board.');
        }
        const newBoard: Board = await response.json();
        await fetchBoards(); // Refresh the list
        return newBoard;
    } catch (err: any) {
        setError(err.message);
        return null;
    } finally {
        setIsLoading(false);
    }
  }, [fetchBoards]);

  // --- Value provided to consumers ---
  const value = {
    companyName,
    teamName,
    mode,
    teamMembers,
    recentActivities,
    toggleColorMode,
    addTeamMembers,
    addActivity,
    clearActivities,
    projects,
    isLoading,
    error,
    fetchProjects,
    fetchProjectById,
    boards,
    fetchBoards,
    createBoard,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};